/**
 * Formulário da ficha técnica por tipo (alterar os dados das bombas e demais) — Nexora Grid (frontend, US-406).
 *
 * Criado em: 16/10/2026 · Última modificação: 18/10/2026 · Versão: 1.1.0
 *
 * GUS-1102: só a engenharia grava; cada gravação é uma versão com motivo; os campos da ficha mínima aparecem
 * marcados; erros de faixa e de curva vêm do backend com o nome do campo.
 * v1.1 (18/10/2026): seções; a seção do poço (NBR 12212/12244) aparece para a bomba submersa e o poço profundo, com o
 * corte esquemático e os avisos de boas práticas (submergência, bomba nos filtros).
 */
import { useEffect, useState } from "react";
import { api, ErroApi } from "../api";
import { CortePoco } from "./CortePoco";
import { Curva } from "./Curva";
import { CAMPOS_FICHA, type Ficha } from "./tipos";

type Valores = Record<string, unknown>;

/** Converte o formulário em JSON: vazio → ausente; números; curvas sem linhas vazias. */
function paraEnvio(valores: Valores, tipo: string): Valores {
  const saida: Valores = {};
  for (const campo of CAMPOS_FICHA[tipo] ?? []) {
    const v = valores[campo.nome];
    if (v === undefined || v === "" || v === null) continue;
    if (campo.tipo === "numero") saida[campo.nome] = Number(v);
    else if (campo.tipo === "curva") {
      const pontos = (v as Valores[]).filter((p) => Object.values(p).every((x) => x !== "" && x !== undefined));
      if (pontos.length) saida[campo.nome] = pontos;
    } else saida[campo.nome] = v;
  }
  return saida;
}

export function FichaForm({ ativoId, tipo, editavel, aoSalvar }: {
  ativoId: string; tipo: string; editavel: boolean; aoSalvar: () => void;
}) {
  const [ficha, setFicha] = useState<Ficha | null>(null);
  const [valores, setValores] = useState<Valores>({});
  const [motivo, setMotivo] = useState("");
  const [mensagem, setMensagem] = useState<{ tipo: "ok" | "erro"; texto: string } | null>(null);
  const [errosCampo, setErrosCampo] = useState<Record<string, string>>({});
  const carregar = () => api<Ficha>(`/v1/ativos/${ativoId}/ficha`).then((f) => { setFicha(f); setValores(f.ficha); });
  useEffect(() => { void carregar(); }, [ativoId]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!ficha) return <p className="nx-suave">carregando ficha…</p>;
  const ehSubmersa = tipo === "poco_profundo" || valores.instalacao === "submersa";
  const campos = (CAMPOS_FICHA[tipo] ?? []).filter((c) => !c.submersa || ehSubmersa);
  const numero = (n: string) => (valores[n] === undefined || valores[n] === "" || valores[n] === null ? undefined : Number(valores[n]));
  const salvar = async () => {
    setMensagem(null);
    setErrosCampo({});
    try {
      await api(`/v1/ativos/${ativoId}/ficha`, { metodo: "PUT", corpo: { versao: ficha.versao, motivo, ficha: paraEnvio(valores, tipo) } });
      setMotivo("");
      await carregar();
      setMensagem({ tipo: "ok", texto: "Ficha salva (nova versão)." });
      aoSalvar();
    } catch (e) {
      if (e instanceof ErroApi && Array.isArray(e.detalhe)) {
        const porCampo: Record<string, string> = {};
        for (const d of e.detalhe as { campo?: string; erro?: string; loc?: string[]; msg?: string }[]) {
          const campo = d.campo ?? d.loc?.slice(-1)[0] ?? "ficha";
          porCampo[campo.split(".")[0]] = d.erro ?? d.msg ?? "inválido";
        }
        setErrosCampo(porCampo);
        setMensagem({ tipo: "erro", texto: "Confira os campos marcados." });
      } else setMensagem({ tipo: "erro", texto: e instanceof Error ? e.message : String(e) });
    }
  };
  const campoFicha = (c: (typeof campos)[number]) => {
    const obrigatorio = ficha.campos_minimos.includes(c.nome);
    const id = `ficha-${c.nome}`;
    const valor = valores[c.nome];
    if (c.tipo === "curva") {
      return (
        <div key={c.nome} className="nx-campo campo-largo" data-obrigatorio={obrigatorio || undefined}>
          <label>{c.rotulo}</label>
          <Curva campo={c} pontos={(valor as Record<string, number | string>[]) ?? []} editavel={editavel}
            aoMudar={(p) => setValores({ ...valores, [c.nome]: p })} />
          {errosCampo[c.nome] && <small className="erro">{errosCampo[c.nome]}</small>}
        </div>
      );
    }
    return (
      <div key={c.nome} className={`nx-campo${c.nome === "observacao" ? " campo-largo" : ""}`}
        data-obrigatorio={obrigatorio || undefined}>
        <label htmlFor={id}>{c.rotulo}{c.unidade ? ` (${c.unidade})` : ""}</label>
        {c.tipo === "opcao" ? (
          <select id={id} disabled={!editavel} value={String(valor ?? "")}
            onChange={(e) => setValores({ ...valores, [c.nome]: e.target.value })}>
            <option value="">—</option>
            {c.opcoes!.map(([v, t]) => <option key={v} value={v}>{t}</option>)}
          </select>
        ) : (
          <input id={id} disabled={!editavel} type={c.tipo === "numero" ? "number" : c.tipo === "data" ? "date" : "text"}
            step="any" value={String(valor ?? "")} onChange={(e) => setValores({ ...valores, [c.nome]: e.target.value })} />
        )}
        {errosCampo[c.nome] && <small className="erro">{errosCampo[c.nome]}</small>}
      </div>
    );
  };
  return (
    <div className="ficha">
      <p className={ficha.completa ? "ficha-ok" : "ficha-pendente"} role="status">
        {ficha.completa ? "Ficha mínima completa." : `Falta para a ficha mínima: ${ficha.pendencias.join(", ")}.`}
        <span className="nx-suave"> · versão {ficha.versao}</span>
      </p>
      {(ficha.avisos ?? []).length > 0 && (
        <ul className="avisos-ficha" role="status">{ficha.avisos!.map((a) => <li key={a}>⚠ {a}</li>)}</ul>
      )}
      <div className="grade-ficha">
        {campos.flatMap((c) => {
          const titulo = c.secao ? [<h4 key={`secao-${c.nome}`} className="secao-ficha campo-largo">{c.secao}</h4>] : [];
          return [...titulo, campoFicha(c)];
        })}
        {ehSubmersa && (
          <div className="campo-largo">
            <CortePoco poco={{ profundidade_poco_m: numero("profundidade_poco_m"), nivel_estatico_m: numero("nivel_estatico_m"),
              nivel_dinamico_m: numero("nivel_dinamico_m"), profundidade_instalacao_m: numero("profundidade_instalacao_m"),
              topo_filtros_m: numero("topo_filtros_m") }} />
          </div>
        )}
      </div>
      {editavel && (
        <div className="acoes-ficha">
          <div className="nx-campo" data-obrigatorio>
            <label htmlFor="motivo-ficha">Motivo da alteração</label>
            <input id="motivo-ficha" value={motivo} onChange={(e) => setMotivo(e.target.value)} placeholder="ex.: dados da placa do motor" />
          </div>
          <button type="button" className="nx-botao nx-botao-principal" disabled={motivo.trim().length < 3} onClick={() => void salvar()}>
            Salvar ficha</button>
        </div>
      )}
      {mensagem && <p className={mensagem.tipo === "ok" ? "ficha-ok" : "erro"} role="alert">{mensagem.texto}</p>}
    </div>
  );
}
